/* eslint-disable max-lines */

import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {
  DataObjectId,
  GroupChat,
  groupChatContainerId,
  Hub,
  isAvailableForManager,
  isDirectlyVisible,
  newsCompanyId,
  noBoardContainerId,
  PermissionObject,
  repeatTemplatesContainerId,
  rootNodeId,
  SandboxTree,
  StorageDepartmentHierarchy,
  Tag,
  VisibilityOptions
} from 'yougile-model'
import {MessageBox} from '@components/message-box'
import {ChatSubscribers} from '@extensions/chat-subscribers'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {globals} from '../extensions/globals-base'
import {Sticker, systemStickerCtl} from '../extensions/sticker/sticker'
import {UserAccess} from '../extensions/user-access'
import {CompanyPreferencesClient} from './company-preferences-client'
import {DepartmentsClient} from './departments-client'
import {PermissionsClientHelper} from './permissions-client'
import {RepeatClient} from './repeat-client'
import {Sync} from './sync'
import {WorkflowClient} from './workflow-client'

const maxCheckRecursion = 10

export class PermissionsChecker {
  static isAdmin(props?: {userId?: DataObjectId; box?: SandboxTree}): boolean {
    const {userId, box} = props ?? {}
    const sb = box ?? Sync.getCurrentSandbox()
    if (!sb?.users) {
      return false
    }
    const user = sb.users.get(userId ?? globals.currentUserId)
    return !!user?.isAdmin
  }

  static isUserInProject(userId: DataObjectId, project: Hub): boolean {
    if (!project) {
      logger.warn('Checking empty hub (2)')
      return false
    }
    const obj = PermissionsClientHelper.getPermissions(
      project.companyId,
      project.id,
      userId
    )
    if (!obj) {
      return false
    }
    return (
      isDirectlyVisible(obj) ||
      !!PermissionsClientHelper.getAllChildObjects(
        project.companyId,
        project.id,
        userId
      ).find(isDirectlyVisible)
    )
  }

  private static checkNoBoard({
    hub,
    checker,
    userId,
    opts,
    ignoreAdmin = false,
    providedHierarchy,
    checkCache,
    visitedRecursively,
    checkCanAssignUser,
    checkWithCheckerOnly
  }: CheckParams): boolean {
    if (!checkCanAssignUser && (opts?.considerAsMy || opts?.considerAsWithMe)) {
      return true
    }
    visitedRecursively ??= new Set()
    hub && visitedRecursively.add(hub.id)
    const parents = hub ? SubtaskUtil.getParentTaskIds(hub.id) : []
    if (checkCanAssignUser && !parents.length) {
      return true
    }

    if (parents.length) {
      const box = Sync.getCurrentSandbox()
      const limitToCheckParents = 4
      let index = 0
      for (const parentId of parents) {
        if (index >= limitToCheckParents) {
          break
        }

        const parent = box.getHub(parentId)
        if (!parent || parent.deleted) {
          continue
        }
        const allowed =
          checkCache?.get(parent.id) ??
          this.check({
            hub: parent,
            checker,
            userId,
            opts: {
              ...opts,
              considerAsMy: undefined,
              considerAsWithMe: undefined
            },
            ignoreAdmin,
            providedHierarchy,
            checkCache,
            visitedRecursively
          })

        if (allowed) {
          return true
        }

        index++
      }

      if (checkCanAssignUser) {
        return false
      }
    }

    if (checkWithCheckerOnly) {
      return false
    }

    if (hub) {
      const hierarchy =
        providedHierarchy ?? DepartmentsClient.getHierarchy(Sync.get(hub))
      const res =
        hub.data.by === (userId || globals.currentUserId) ||
        isAvailableForManager(userId || globals.currentUserId, hub, hierarchy)
      checkCache?.set(hub.id, res)
      return res
    }

    return false
  }

  private static check({
    hub,
    checker,
    userId,
    opts = {},
    ignoreAdmin = false,
    providedHierarchy,
    checkCache,
    visitedRecursively,
    checkCanAssignUser,
    checkWithCheckerOnly
  }: CheckParams): boolean {
    if (!hub) {
      return false
    }
    if (hub.companyId === newsCompanyId) {
      return true
    }
    if (hub.location === repeatTemplatesContainerId) {
      const box = Sync.get(hub)
      return RepeatClient.canEditTemplate(box, hub)
    }
    if (hub.data?.private && hub.data.private !== globals.currentUserId) {
      return false
    }
    if (!ignoreAdmin && PermissionsChecker.isAdmin({userId})) {
      return true
    }
    if (
      visitedRecursively?.has(hub.id) ||
      (visitedRecursively?.size ?? 0) > maxCheckRecursion
    ) {
      logger.log('Recursive subtasks in check', hub.id)
      return false
    }

    if (opts?.considerAsWithMe === undefined && hub) {
      const subscribedIds = ChatSubscribers.getSubscribedUserIds(hub)
      opts.considerAsWithMe = subscribedIds.includes(
        userId || globals.currentUserId
      )
    }

    if (hub.location === noBoardContainerId) {
      return this.checkNoBoard({
        hub,
        checker,
        userId,
        opts,
        ignoreAdmin,
        providedHierarchy,
        checkCache,
        visitedRecursively,
        checkCanAssignUser,
        checkWithCheckerOnly
      })
    }

    const obj = PermissionsClientHelper.getPermissions(
      hub.companyId,
      hub.id,
      userId || globals.currentUserId,
      opts
    )
    const res = obj?.permissions ? checker(obj) : false
    checkCache?.set(hub.id, res)
    return res
  }

  static checkDeletion(hub: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(hub, 'deleted')) {
      return false
    }
    return PermissionsChecker.check({
      hub,
      checker: obj => obj.permissions.delete
    })
  }

  static checkRename(hub: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(hub, 'title')) {
      return false
    }
    return PermissionsChecker.check({
      hub,
      checker: obj => obj.permissions.editTitle
    })
  }

  static checkComplete(task: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'completed')) {
      return false
    }
    return PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' &&
        (obj.permissions.complete || obj.permissions.complete === undefined)
    })
  }

  static shouldFallbackToOldCheckComplete(task: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'completed')) {
      return false
    }
    let wasChecked = false
    return (
      PermissionsChecker.check({
        hub: task,
        checker: obj => {
          wasChecked = true
          return obj.type === 'task' && obj.permissions.complete === undefined
        },
        checkWithCheckerOnly: true
      }) && wasChecked
    )
  }

  static oldCheckComplete(task: Hub): boolean {
    const box = Sync.get(task)
    const project = box.getProjectFor(task)
    if (
      PermissionsChecker.isAdmin() ||
      (project &&
        UserAccess.getRoleInProject(globals.currentUserId, project) === 'admin')
    ) {
      return true
    }

    const assigned = Sticker.getUsers(task)
    if (assigned.includes(globals.currentUserId)) {
      return true
    }

    if (
      assigned.length > 0 ||
      !PermissionsChecker.checkAssignUser(task, globals.currentUserId)
    ) {
      return false
    }
    return true
  }

  static checkEditDescription(task: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'description')) {
      return false
    }
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.editDescription
    })
  }

  static checkSendMessages(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.sendMessages
    })
  }

  static checkSendFiles(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.sendFiles
    })
  }

  static checkAddChildTo(hub: Hub): boolean {
    if (hub?.id === hub?.companyId) {
      return CompanyPreferencesClient.isAllowedForCurrentUser(
        hub.companyId,
        'createProjects'
      )
    }
    if (WorkflowClient.isDisallowedByRestriction(hub, 'create', 'column')) {
      return false
    }
    return PermissionsChecker.check({
      hub,
      checker: obj => {
        switch (obj.type) {
          case 'project':
            return obj.permissions.addBoard
          case 'board':
            return obj.permissions.addColumn
          case 'column':
            return obj.permissions.addTask
          default:
            return false
        }
      }
    })
  }

  static checkStartMove(hub: Hub): boolean {
    return PermissionsChecker.check({
      hub,
      checker: obj => {
        switch (obj.type) {
          case 'project':
            return false
          case 'board':
            return obj.permissions.move
          case 'column':
            return obj.permissions.move !== 'no'
          case 'task':
            return obj.permissions.move !== 'no'
          default:
            return false
        }
      }
    })
  }

  private static checkMoveByCustomRole(item: Hub, from: Hub, to: Hub): boolean {
    const box = Sync.get(item)
    const roleTo = UserAccess.getRoleInProject(globals.currentUserId, to)
    const roleFrom = UserAccess.getRoleInProject(globals.currentUserId, from)
    const directTo = roleTo === 'admin' || roleTo === 'worker'
    const directFrom = roleFrom === 'admin' || roleFrom === 'worker'
    const objItem = PermissionsClientHelper.getPermissions(
      item.companyId,
      item.id,
      globals.currentUserId
    )
    const objFrom = PermissionsClientHelper.getPermissions(
      item.companyId,
      from.id,
      globals.currentUserId
    )
    const objTo = PermissionsClientHelper.getPermissions(
      item.companyId,
      to.id,
      globals.currentUserId
    )
    if (
      !objItem ||
      (!objItem.permissions && !directFrom) ||
      !objFrom ||
      (!objFrom.permissions && !directFrom) ||
      !objTo ||
      (!objTo.permissions && !directTo)
    ) {
      return false
    }
    if (
      objItem.type === 'task' &&
      objFrom.type === 'column' &&
      objTo.type === 'column'
    ) {
      if (objItem.permissions.move === 'yes') {
        return true
      } else if (objItem.permissions.move === 'board') {
        return from.location === to.location
      } else if (objItem.permissions.move === 'project') {
        const pFrom = box.getProjectFor(from)
        const pTo = box.getProjectFor(to)
        return Boolean(pFrom && pTo && pFrom.id === pTo.id)
      } else {
        return false
      }
    } else if (
      objItem.type === 'column' &&
      objFrom.type === 'board' &&
      objTo.type === 'board'
    ) {
      if (objItem.permissions.move === 'yes') {
        return true
      } else if (objItem.permissions.move === 'project') {
        return from.location === to.location
      } else {
        return false
      }
    } else if (
      objItem.type === 'board' &&
      objFrom.type === 'project' &&
      objTo.type === 'project'
    ) {
      return objItem.permissions.move
    } else {
      return false
    }
  }

  static checkMove(item: Hub, from: Hub, to: Hub): boolean {
    if (!item || !from || !to) {
      logger.warn('Checking empty hub (4)')
      return false
    }

    const box = Sync.get(item)
    const pFrom = box.getProjectFor(from)
    const pTo = box.getProjectFor(to)

    if (pFrom && pTo && pFrom.id !== pTo.id) {
      const canAdd = PermissionsChecker.checkAddChildTo(to)
      if (!canAdd) {
        return false
      }
    }

    if (from.id === noBoardContainerId) {
      return true
    }

    if (WorkflowClient.isDisallowedByRestriction(item, 'location')) {
      MessageBox.warn(Lang.msg.workflow.moveDisallowed)
      return false
    }
    if (PermissionsChecker.isAdmin()) {
      return true
    }
    const roleTo = UserAccess.getRoleInProject(globals.currentUserId, to)
    const roleFrom = UserAccess.getRoleInProject(globals.currentUserId, from)
    const directTo = roleTo === 'admin' || roleTo === 'worker'
    const directFrom = roleFrom === 'admin' || roleFrom === 'worker'
    if (directTo && directFrom) {
      return true
    }

    const res = this.checkMoveByCustomRole(item, from, to)
    return res
  }

  static checkMoveFrom(item: Hub, from: Hub): boolean {
    if (!item || !from) {
      logger.warn('Checking empty hub (4)')
      return false
    }
    if (from.id === noBoardContainerId || PermissionsChecker.isAdmin()) {
      return true
    }
    const roleFrom = UserAccess.getRoleInProject(globals.currentUserId, from)
    const directFrom = roleFrom === 'admin' || roleFrom === 'worker'
    return directFrom || this.checkMoveByCustomRole(item, from, from)
  }

  static makeCheckCache(): Map<DataObjectId, boolean> {
    return new Map()
  }

  static checkShow(
    hierarchy: StorageDepartmentHierarchy | 'ignore-departments',
    hub: Hub | undefined,
    opts: VisibilityOptions & {
      userId?: DataObjectId
      ignoreAdmin?: boolean
      hasNoChildrenHint?: boolean
      // for better performance when calling `checkShow` from loop
      calculatedHierarchy?: StorageDepartmentHierarchy
      checkCache?: Map<DataObjectId, boolean>
      checkCanAssignUser?: boolean
    }
  ): boolean {
    const {
      userId = globals.currentUserId,
      ignoreAdmin = false,
      hasNoChildrenHint = false,
      checkCache,
      checkCanAssignUser,
      ...visibilityOpts
    } = opts ?? {}
    if (!hub) {
      return false
    }
    if (hub.location === groupChatContainerId) {
      return !!GroupChat.getParticipants(hub)?.[userId]
    }

    // allow system objects
    if (hub.id === rootNodeId || hub.id === hub.companyId) {
      return true
    }
    if (hub.title.startsWith('.')) {
      return true
    }
    //

    if (
      hierarchy !== 'ignore-departments' &&
      isAvailableForManager(userId, hub, hierarchy)
    ) {
      checkCache?.set(hub.id, true)
      return true
    }
    if (!ignoreAdmin && PermissionsChecker.isAdmin({userId})) {
      checkCache?.set(hub.id, true)
      return true
    }
    const res =
      PermissionsChecker.check({
        hub,
        checker: isDirectlyVisible,
        userId,
        opts: visibilityOpts,
        ignoreAdmin,
        providedHierarchy:
          hierarchy === 'ignore-departments'
            ? opts?.calculatedHierarchy
            : hierarchy,
        checkCache,
        visitedRecursively: undefined,
        checkCanAssignUser
      }) ||
      (!hasNoChildrenHint &&
        hub &&
        !!PermissionsClientHelper.getAllChildObjects(
          hub.companyId,
          hub.id,
          userId
        ).find(isDirectlyVisible))
    checkCache?.set(hub.id, res)
    return res
  }

  static checkBoardSettings(board: Hub): boolean {
    return PermissionsChecker.check({
      hub: board,
      checker: obj => obj.type === 'board' && obj.permissions.settings
    })
  }

  static checkEditBoardStickers(board: Hub): boolean {
    return PermissionsChecker.check({
      hub: board,
      checker: obj =>
        obj.type === 'board' &&
        obj.permissions.editStickers &&
        obj.permissions.showStickers
    })
  }

  static checkShowBoardStickers(board: Hub): boolean {
    return PermissionsChecker.check({
      hub: board,
      checker: obj => obj.type === 'board' && obj.permissions.showStickers
    })
  }

  static checkArchive(task: Hub): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'archived')) {
      return false
    }
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.close
    })
  }

  static checkAssignUser(task: Hub, userId: DataObjectId | undefined): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'assignedTo')) {
      return false
    }
    return PermissionsChecker.check({
      hub: task,
      checker: obj => {
        if (obj.type !== 'task') {
          return false
        }
        switch (obj.permissions.assignUsers) {
          case 'no':
            return false
          case 'yes':
            return true
          case 'add-self': {
            const box = Sync.getCurrentSandbox()
            const sticker = systemStickerCtl.User.getSticker(box)
            const fromId = task.stickers[sticker.id]
            return !fromId && userId === globals.currentUserId
          }
          case 'set-self':
            return userId === globals.currentUserId
          case 'change-from-self': {
            const box = Sync.getCurrentSandbox()
            const sticker = systemStickerCtl.User.getSticker(box)
            const fromId = task.stickers[sticker.id]
            return fromId === globals.currentUserId
          }
          default:
            return false
        }
      }
    })
  }

  static checkEditTaskStickers(
    task: Hub | undefined,
    sticker: Tag | 'not-user',
    state?: string
  ): boolean {
    if (!task || !sticker) {
      return false
    }
    const box = Sync.getCurrentSandbox()
    const userSticker = systemStickerCtl.User.getSticker(box)
    if (
      sticker !== 'not-user' &&
      userSticker &&
      userSticker.id === sticker.id
    ) {
      return PermissionsChecker.checkAssignUser(task, state as DataObjectId)
    } else {
      if (Task.isTaskTemplate(task)) {
        return true
      }
      return PermissionsChecker.check({
        hub: task,
        checker: obj => obj.type === 'task' && obj.permissions.editStickers
      })
    }
  }

  static checkEditUserSticker(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' && obj.permissions.assignUsers !== 'no'
    })
  }

  static checkConnect(task1: Hub, task2: Hub): boolean {
    return PermissionsChecker.check({
      hub: task1,
      checker: obj1 =>
        obj1.type === 'task' &&
        obj1.permissions.connect &&
        PermissionsChecker.check({
          hub: task2,
          checker: obj2 => obj2.type === 'task' && obj2.permissions.connect
        })
    })
  }

  static checkConnectSingle(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.connect
    })
  }

  static checkEditSubtasks(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' && obj.permissions.editSubtasks === 'yes'
    })
  }

  static checkCompleteSubtasks(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' &&
        (obj.permissions.editSubtasks === 'yes' ||
          obj.permissions.editSubtasks === 'complete')
    })
  }

  static checkEditPins(task: Hub): boolean {
    return PermissionsChecker.check({
      hub: task,
      checker: obj => obj.type === 'task' && obj.permissions.editPins
    })
  }

  static checkEditWhoToNotify(task: Hub, userId: DataObjectId): boolean {
    if (WorkflowClient.isDisallowedByRestriction(task, 'subscribers')) {
      return false
    }
    return PermissionsChecker.check({
      hub: task,
      checker: obj => {
        if (obj.type !== 'task') {
          return false
        }
        switch (obj.permissions.editWhoToNotify) {
          case 'yes':
            return true
          case 'no':
            return false
          case 'self':
            return userId === globals.currentUserId
          default:
            return false
        }
      }
    })
  }

  static editWhoToNotifyMode(task: Hub | undefined): 'yes' | 'no' | 'self' {
    if (WorkflowClient.isDisallowedByRestriction(task, 'subscribers')) {
      return 'no'
    }
    const yes = PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' && obj.permissions.editWhoToNotify === 'yes'
    })
    if (yes) {
      return 'yes'
    }
    const self = PermissionsChecker.check({
      hub: task,
      checker: obj =>
        obj.type === 'task' && obj.permissions.editWhoToNotify === 'self'
    })
    return self ? 'self' : 'no'
  }
}

interface CheckParams {
  hub: Hub | undefined
  checker: (obj: PermissionObject) => boolean
  userId?: DataObjectId
  opts?: VisibilityOptions
  ignoreAdmin?: boolean
  providedHierarchy?: StorageDepartmentHierarchy
  checkCache?: Map<DataObjectId, boolean>
  visitedRecursively?: Set<DataObjectId>
  checkCanAssignUser?: boolean
  checkWithCheckerOnly?: boolean
}
